<template>
  <div class="workout-legacy-timer px-1 w-100">
    <template v-if="$slots.default && !isCountingIn">
      <div class="p-1">
        <slot></slot>
      </div>
    </template>

    <div
      :class="[
        { '--centered': isCountingIn, '--hide-timer': hideTimer },
        'workout-legacy-timer__wrapper p-1 mb-1'
      ]"
    >
      <div
        :class="[
          {
            '--counting-in': isCountingIn,
            '--small': !isCountingIn,
            '--paused': isWorkoutTimer(TIMER_STATUS.PAUSED)
          },
          'workout-legacy-timer__element cta-row__element'
        ]"
      >
        <h6 v-if="!isCountingIn" class="workout-legacy-timer__title mb-0 mt-0">
          Total
        </h6>
        <div
          data-cy="total-timer"
          class="workout-legacy-timer__time text-mono-numbers"
        >
          {{ workoutTimer.humanTime }}
        </div>
      </div>

      <div
        :class="[
          {
            'd-none': isCountingIn,
            '--running': isGroupTimer(TIMER_STATUS.RUNNING),
            '--paused': isGroupTimer(TIMER_STATUS.PAUSED),
            '--finished': isGroupTimer(TIMER_STATUS.FINISHED)
          },
          'workout-legacy-timer__element cta-row__element'
        ]"
        @click="toggleTimer"
      >
        <h6 v-if="!isCountingIn" class="workout-legacy-timer__title mb-0 mt-0">
          Current
        </h6>
        <template v-if="currentTimer">
          <div
            data-cy="current-timer"
            class="workout-legacy-timer__time text-mono-numbers d-flex align-items-center"
          >
            {{ currentTimer.humanTime }}
            <v-icon
              v-if="isGroupTimer(TIMER_STATUS.RUNNING)"
              icon="pause"
              class="workout-legacy-timer__icon ml-1"
            />
            <v-icon
              v-if="isGroupTimer(TIMER_STATUS.PAUSED)"
              icon="play_arrow"
              class="workout-legacy-timer__icon ml-1"
            />
          </div>
        </template>
        <template v-else-if="$slots.current">
          <slot name="current" />
        </template>
        <template v-else-if="!hideTimer">
          <div class="workout-legacy-timer__time">
            <infinity-loader />
          </div>
        </template>

        <div v-if="$slots.appendage" class="workout-legacy-timer__appendage">
          <slot name="appendage" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TIMER_STATUS } from "@/lib/timer"
import EventBus, { EVENTS } from "@/lib/event-bus"

import InfinityLoader from "@/components/workout/InfinityLoader"

export default {
  props: {
    workoutTimer: { type: Object, required: true },
    currentTimer: { type: Object, default: null },
    hideTimer: { type: Boolean, default: false }
  },

  components: {
    InfinityLoader
  },

  data() {
    return {
      TIMER_STATUS
    }
  },

  mounted() {
    EventBus.$on(EVENTS.WORKOUT_EXIT_ATTEMPTED, this.pauseTimers)
    EventBus.$on(EVENTS.WORKOUT_RESUMED, this.resumeTimers)
  },

  methods: {
    isGroupTimer(status) {
      return this.isTimerStatus(this.currentTimer, status)
    },

    isWorkoutTimer(status) {
      return this.isTimerStatus(this.workoutTimer, status)
    },

    isTimerStatus(timer, status) {
      return !!timer && timer.status === status
    },

    toggleTimer() {
      if (!this.currentTimer) return

      if (
        this.isGroupTimer(TIMER_STATUS.COUNTING_IN) ||
        this.isGroupTimer(TIMER_STATUS.FINISHED)
      ) {
        return
      }

      if (this.isGroupTimer(TIMER_STATUS.PAUSED)) {
        this.currentTimer.start()
      } else {
        this.currentTimer.pause()
      }
    },

    pauseTimers() {
      if (this.currentTimer) this.currentTimer.pause()
      this.workoutTimer.pause()
    },

    resumeTimers() {
      if (this.currentTimer) this.currentTimer.start()
      this.workoutTimer.start()
    }
  },

  computed: {
    isCountingIn() {
      return (
        this.isWorkoutTimer(TIMER_STATUS.COUNTING_IN) ||
        this.isWorkoutTimer(TIMER_STATUS.INITIALIZED)
      )
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.workout-legacy-timer__wrapper {
  display: grid;
  grid-template-columns: 8rem auto;
  align-items: flex-end;
  animation: fade-in-up 0.4s ease;
  pointer-events: none;
  font-family: $system-font-stack;

  &.--centered {
    justify-content: center;

    .workout-legacy-timer__element {
      transform-origin: 50% 100%;
    }

    .workout-legacy-timer__time {
      justify-content: center;
    }
  }

  &.--hide-timer {
    grid-template-columns: auto;

    .workout-legacy-timer__element:first-child {
      display: none;
    }

    &.--centered {
      .workout-legacy-timer__element:first-child {
        display: flex;
      }
    }
  }
}

.workout-legacy-timer__time {
  font-size: $font-size--h1 + $font-size--display-3;
  line-height: $size--9;
  font-weight: $font-weight--x-bold;
  color: $color-ui--grey-90;
  transform-origin: 0% 100%;
  transition: transform 0.2s ease, color 0.3s ease;
  min-height: 44px;
  display: flex;
  letter-spacing: -1px;
}

h6.workout-legacy-timer__title {
  font-weight: $font-weight--bold;
  color: $color-ui--grey-50;
  padding-bottom: $size--1;
}

.workout-legacy-timer__icon {
  font-size: $size--6;
  height: $size--6;
  opacity: 0.6;
}

.workout-legacy-timer__element {
  position: relative;
  opacity: 1;
  pointer-events: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  &.--small {
    .workout-legacy-timer__time {
      font-size: $font-size--h1;
      line-height: $size--8;
      position: relative;
      top: -1px;
      align-items: flex-end;
    }
  }

  &.--running,
  &.--counting-in {
    color: var(--brand-color-hex);
    border-color: var(--brand-color-hex);

    h1 {
      color: var(--brand-color-hex);
    }
  }

  &.--paused,
  &.--finished {
    .workout-legacy-timer__time {
      transform: scale(0.9);
      opacity: 0.5;
    }
  }

  &.--paused {
    color: $color-ui--grey-60;
    border-color: $color-ui--grey-60;
  }

  &.--finished {
    color: $color-ui--grey-40;
    border-color: $color-ui--grey-40;
  }
}

.workout-legacy-timer__appendage {
  position: absolute;
  bottom: -$size--5;
}
</style>
